import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useEmailStore } from '@/stores/useEmailStore';
import { Plus, Send, TestTube, Loader2, Pencil } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_VARIANT = {
  draft: 'secondary',
  sending: 'outline',
  sent: 'default',
  failed: 'destructive',
  cancelled: 'outline',
};

export function CampaignList() {
  const {
    campaigns, campaignsLoading, fetchCampaigns,
    createCampaign, updateCampaign,
    sendCampaign, sending,
    programs, fetchPrograms,
  } = useEmailStore();

  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newType, setNewType] = useState('campaign');
  const [newProgramId, setNewProgramId] = useState('');
  const [newSubject, setNewSubject] = useState('');
  const [newBody, setNewBody] = useState('');

  const [editingCampaign, setEditingCampaign] = useState(null);
  const [editSubject, setEditSubject] = useState('');
  const [editBody, setEditBody] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchCampaigns();
    fetchPrograms();
  }, [fetchCampaigns, fetchPrograms]);

  const handleCreate = async () => {
    if (!newName.trim()) { toast.error('Name is required'); return; }
    if (!newSubject.trim()) { toast.error('Subject line is required'); return; }
    if (!newBody.trim()) { toast.error('Email body is required'); return; }
    try {
      const filter = newProgramId ? { program_id: newProgramId } : {};
      await createCampaign({
        name: newName,
        type: newType,
        subject: newSubject,
        body_html: newBody.includes('<') ? newBody : `<p>${newBody.replace(/\n/g, '<br/>')}</p>`,
        target_filter: filter,
      });
      setShowCreate(false);
      setNewName('');
      setNewSubject('');
      setNewBody('');
      setNewProgramId('');
      toast.success('Campaign created');
    } catch (err) {
      toast.error(`Failed: ${err.message}`);
    }
  };

  const handleEdit = (campaign) => {
    setEditingCampaign(campaign);
    setEditSubject(campaign.subject || campaign.name);
    setEditBody(campaign.body_html || '');
  };

  const handleSaveEdit = async () => {
    if (!editSubject.trim() || !editBody.trim()) {
      toast.error('Subject and body are required');
      return;
    }
    setSaving(true);
    try {
      const body = editBody.includes('<') ? editBody : `<p>${editBody.replace(/\n/g, '<br/>')}</p>`;
      await updateCampaign(editingCampaign.id, { subject: editSubject, body_html: body });
      setEditingCampaign(null);
      toast.success('Campaign updated');
    } catch (err) {
      toast.error(`Save failed: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleSend = async (campaignId, isTest) => {
    try {
      const result = await sendCampaign(campaignId, isTest);
      if (isTest) {
        toast.success('Test emails sent to your inbox');
      } else {
        toast.success(`Campaign sent: ${result.sent || 0} delivered`);
      }
      fetchCampaigns();
    } catch (err) {
      toast.error(`Send failed: ${err.message}`);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base">Campaigns</CardTitle>
            <CardDescription>Create and manage email campaigns.</CardDescription>
          </div>
          <Dialog open={showCreate} onOpenChange={setShowCreate}>
            <DialogTrigger asChild>
              <Button size="sm">
                <Plus className="h-4 w-4 mr-2" />
                New Campaign
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>New Campaign</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-2">
                <div className="space-y-1.5">
                  <Label className="text-xs">Campaign name</Label>
                  <Input
                    placeholder="e.g. May Progress Check-in"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Type</Label>
                    <Select value={newType} onValueChange={setNewType}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="campaign">Campaign</SelectItem>
                        <SelectItem value="coaching">Coaching</SelectItem>
                        <SelectItem value="progress">Progress Report</SelectItem>
                        <SelectItem value="reminder">Reminder</SelectItem>
                        <SelectItem value="onboarding">Onboarding</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Target program</Label>
                    <Select value={newProgramId} onValueChange={setNewProgramId}>
                      <SelectTrigger>
                        <SelectValue placeholder="All users" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="">All users</SelectItem>
                        {programs.map((p) => (
                          <SelectItem key={p.program_id} value={p.program_id}>
                            {p.program_name} ({p.active_user_count} users)
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Subject line</Label>
                  <Input
                    placeholder="Email subject line"
                    value={newSubject}
                    onChange={(e) => setNewSubject(e.target.value)}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Email body (HTML or plain text)</Label>
                  <Textarea
                    placeholder="Write your email body here..."
                    value={newBody}
                    onChange={(e) => setNewBody(e.target.value)}
                    rows={6}
                    className="text-sm"
                  />
                </div>
                <Button onClick={handleCreate} className="w-full">
                  Create Campaign
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        {campaignsLoading ? (
          <p className="text-sm text-muted-foreground py-8 text-center">Loading...</p>
        ) : campaigns.length === 0 ? (
          <p className="text-sm text-muted-foreground py-8 text-center">
            No campaigns yet. Create your first one.
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Recipients</TableHead>
                <TableHead>Opens</TableHead>
                <TableHead>Date</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {campaigns.map((c) => (
                <TableRow key={c.id}>
                  <TableCell className="font-medium">{c.name}</TableCell>
                  <TableCell>
                    <Badge variant="outline">{c.type}</Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant={STATUS_VARIANT[c.status]}>{c.status}</Badge>
                  </TableCell>
                  <TableCell>{c.total_recipients || 0}</TableCell>
                  <TableCell>
                    {c.sent_count > 0
                      ? `${c.opened_count}/${c.sent_count}`
                      : '-'}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {c.sent_at
                      ? new Date(c.sent_at).toLocaleDateString()
                      : new Date(c.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell className="text-right">
                    {c.status === 'draft' && (
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleEdit(c)}
                        >
                          <Pencil className="h-3.5 w-3.5 mr-1" />
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleSend(c.id, true)}
                          disabled={sending || !c.body_html}
                          title={!c.body_html ? 'Edit campaign to add email body first' : ''}
                        >
                          <TestTube className="h-3.5 w-3.5 mr-1" />
                          Test
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleSend(c.id, false)}
                          disabled={sending || !c.body_html}
                          title={!c.body_html ? 'Edit campaign to add email body first' : ''}
                        >
                          <Send className="h-3.5 w-3.5 mr-1" />
                          Send
                        </Button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>

      <Dialog open={!!editingCampaign} onOpenChange={(open) => { if (!open) setEditingCampaign(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Campaign — {editingCampaign?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="space-y-1.5">
              <Label className="text-xs">Subject line</Label>
              <Input
                placeholder="Email subject line"
                value={editSubject}
                onChange={(e) => setEditSubject(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Email body (HTML or plain text)</Label>
              <Textarea
                placeholder="Write your email body here..."
                value={editBody}
                onChange={(e) => setEditBody(e.target.value)}
                rows={10}
                className="text-sm font-mono"
              />
            </div>
            <Button onClick={handleSaveEdit} disabled={saving} className="w-full">
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Save Changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
